import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../context/UserContext';
import API from '../config/axiosConfig';
import AppService from '../services/AppService';
import DropdownWithIcon from './DropdownWithIcon';
import InputWithIcon from './InputWithIcon';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { validateFile } from '../utils/file-validation';
import '../pages/Settings.scss';
import './AppConfigSettings.scss';

const IMPORT_ACCEPT = { 'application/json': ['.json'] };
const IMPORT_MAX_SIZE = 10 * 1024 * 1024;

interface AppConfig {
  appName: string;
  configName: string;
  hospital: string;
  createdAt?: string;
  updatedAt?: string;
}

interface App {
  id: number;
  key: string;
  title: string;
}

interface AppData {
  [appName: string]: {
    [configName: string]: {
      [hospital: string]: AppConfig | null;
    };
  };
}

const AppConfigSettings: React.FC = () => {
  const { hospitals, user } = useContext(UserContext);
  const [appData, setAppData] = useState<AppData>({});
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newConfigName, setNewConfigName] = useState<string>('');
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  // Identifies the row currently importing, so only that button shows progress.
  const [importing, setImporting] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ appName: string, configName: string, hospital: string } | null>(null);

  // Import failures outlive the other messages here (6s rather than 3s): they
  // carry a reason the user has to read, not just an acknowledgement.
  const showImportError = (message: string) => {
    setError(message);
    setTimeout(() => setError(''), 6000);
  };

  // INNER (config) collapse state
  const [expandedConfig, setExpandedConfig] = useState<string | null>(null);
  const toggleConfig = (configIdentifier: string) => {
    setExpandedConfig(expandedConfig === configIdentifier ? null : configIdentifier);
  };

  // OUTER (app) collapse state
  const [expandedApp, setExpandedApp] = useState<string | null>(null);
  const toggleApp = (appName: string) => {
    setExpandedApp(expandedApp === appName ? null : appName);
  };

  useEffect(() => {
    fetchConfigs();
    fetchApps();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await API.get('/api/configs');

      // Transform the data to our hierarchical format
      const transformedData: AppData = {};

      response.data.data.forEach((config: AppConfig) => {
        // Initialize app if not exists
        if (!transformedData[config.appName]) {
          transformedData[config.appName] = {};
        }

        // Initialize config if not exists
        if (!transformedData[config.appName][config.configName]) {
          transformedData[config.appName][config.configName] = {};
        }

        // Add hospital configuration
        transformedData[config.appName][config.configName][config.hospital] = config;
      });

      setAppData(transformedData);
    } catch (err) {
      console.error('Error fetching configurations:', err);
      setError('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const fetchApps = async () => {
    try {
      const appsData = await AppService.getAllApps();
      setApps(appsData);
    } catch (err) {
      console.error('Error fetching apps:', err);
      // Don't set error for apps fetch failure, just log it
    }
  };

  // Handle file import.
  //
  // Every failure here used to be invisible: JSON.parse threw inside the
  // FileReader callback and the rejected API call became an unhandled promise,
  // so picking the wrong file did nothing at all — no message, no change, no
  // hint that anything had been attempted.
  const handleImport = async (appName: string, configName: string, hospital: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    // Reset immediately so re-picking the same file after a fix still fires a
    // change event.
    input.value = '';
    if (!file) return;

    setError('');
    setSuccessMessage('');

    const rejection = validateFile(file, { accept: IMPORT_ACCEPT, maxSize: IMPORT_MAX_SIZE });
    if (rejection) {
      showImportError(`${rejection.message} יש לבחור קובץ JSON שיוצא מהמערכת.`);
      return;
    }

    setImporting(`${appName}/${configName}/${hospital}`);
    try {
      const fileContent = JSON.parse(await file.text());

      await API.post('/api/configs/import', {
        appName,
        configName,
        hospital,
        data: fileContent
      }, { handlingErrorLocally: true });

      setSuccessMessage(`הקובץ יובא בהצלחה ל-${appName}/${configName}/${hospital}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Refresh the list
      fetchConfigs();
    } catch (err: any) {
      console.error('Error importing configuration:', err);
      showImportError(
        err instanceof SyntaxError
          ? `הקובץ "${file.name}" אינו קובץ JSON תקין.`
          : (err?.serverError?.hebMessage || `ייבוא הקובץ ל-${appName}/${configName}/${hospital} נכשל.`)
      );
    } finally {
      setImporting(null);
    }
  };

    // Handle file export
  const handleExport = async (appName: string, configName: string, hospital: string) => {
    try {
      const response = await API.get(`/api/configs/export/${appName}/${configName}/${hospital}`);
      // console.log ('debug - export', {appName, configName, hospital});
      // Create downloadable file
      const fileContent = JSON.stringify(response.data.data, null, 2);
      const blob = new Blob([fileContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `${appName}-${configName}-${hospital}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSuccessMessage('הקובץ יוצא בהצלחה');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error exporting file:', err);
      setError('שגיאה בייצוא הקובץ');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Handle deleting config
  const handleDelete = async (appName: string, configName: string, hospital: string) => {
    setDeleteConfirmation({ appName, configName, hospital });
  };

  // Confirm deletion
  const confirmDelete = async () => {
    if (!deleteConfirmation) return;

    const { appName, configName, hospital } = deleteConfirmation;
    try {
      await API.delete(`/api/configs/${appName}/${configName}/${hospital}`);

      setSuccessMessage(`התצורה נמחקה בהצלחה: ${appName}/${configName}/${hospital}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Update local state to remove the deleted config
      const newAppData = { ...appData };
      if (newAppData[appName] &&
          newAppData[appName][configName] &&
          newAppData[appName][configName][hospital]) {
        delete newAppData[appName][configName][hospital];

        // Clean up empty objects
        if (Object.keys(newAppData[appName][configName]).length === 0) {
          delete newAppData[appName][configName];
        }
        if (Object.keys(newAppData[appName]).length === 0) {
          delete newAppData[appName];
        }

        setAppData(newAppData);
      }
    } catch (err) {
      console.error('Error deleting configuration:', err);
      setError('שגיאה במחיקת התצורה');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteConfirmation(null);
    }
  };

  const cancelDelete = () => setDeleteConfirmation(null);

  
  // Handle adding new config to selected app
  const handleAddConfig = () => {
    if (!selectedApp) {
      setError('יש לבחור אפליקציה תחילה');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!newConfigName.trim()) {
      setError('שם התצורה לא יכול להיות ריק');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    // Initialize app in appData if it doesn't exist
    if (!appData[selectedApp]) {
      setAppData(prev => ({
        ...prev,
        [selectedApp]: {}
      }));
    }

    // Check if config already exists for this app
    if (appData[selectedApp] && appData[selectedApp][newConfigName.trim()]) {
      setError('תצורה בשם זה כבר קיימת באפליקציה זו');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Add new config to the selected app
    setAppData(prev => ({
      ...prev,
      [selectedApp]: {
        ...prev[selectedApp],
        [newConfigName.trim()]: {}
      }
    }));

    setNewConfigName('');
    setSuccessMessage('התצורה נוספה בהצלחה');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Show available hospitals with the default "apps" added
  const availableHospitals = [
    { name: 'apps' },
    ...(hospitals.map((hospital) => ({ name: hospital.nameEnglish })) ?? [])
  ];

  return (
    <div className="app-config-settings">
      {loading ? (
        <div className="loading">טוען נתונים...</div>
      ) : (
        <>
          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <section className="settings-section">
            <h2 className="section-title">הגדרות אפליקציות</h2>

            <div className="add-new-container">
              <div className="add-config-form">
                <DropdownWithIcon
                  placeholder="בחר אפליקציה"
                  value={selectedApp}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedApp(e.target.value)}
                  required={true}
                >
                  <option value="">בחר אפליקציה</option>
                  {apps.map((app) => (
                    <option key={app.key} value={app.key}>
                      {app.title} ({app.key})
                    </option>
                  ))}
                </DropdownWithIcon>
                <InputWithIcon
                  placeholder="שם תצורה חדשה"
                  value={newConfigName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setNewConfigName(e.target.value)}
                  disabled={!selectedApp}
                  required={true}
                />
                <button
                  className="button-primary"
                  onClick={handleAddConfig}
                  disabled={!selectedApp || !newConfigName.trim()}
                >
                  הוסף תצורה
                </button>
              </div>
            </div>

            <div className="apps-list">              
              {Object.keys(appData).length === 0 ? (
                <div className="no-data">לא נמצאו אפליקציות מוגדרות</div>
              ) : (
                Object.entries(appData).map(([appName, configs]) => {
                  const app = apps.find(a => a.key === appName);
                  const displayName = app ? `${app.title} (${app.key})` : appName;
                  const appIsOpen = expandedApp === appName;

                  return (
                  <div className="app-settings-config-wrapper">
                    <div key={appName} className="collapsible outer-collapsible">
                      <div className="collapsible-header" onClick={() => toggleApp(appName)}>
                        <h4 className="app-name">{displayName}</h4>
                        <span className={`collapse-icon ${appIsOpen ? 'open' : ''}`}>
                          {appIsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </span>
                      </div>

                      {appIsOpen && (
                        <div className="collapsible-content">
                          <div className="configs-list">
                            {Object.keys(configs).length === 0 ? (
                              <div className="no-data">לא נמצאו תצורות לאפליקציה זו</div>
                            ) : (
                              Object.entries(configs).map(([configName, hospitals]) => {
                                const configKey = `${appName}-${configName}`;
                                const configIsOpen = expandedConfig === configKey;

                                return (
                                  <div key={configName} className="collapsible inner-collapsible">
                                    <div className="collapsible-header" onClick={() => toggleConfig(configKey)}>
                                      <h5 className="config-name">{configName}</h5>
                                      <span className={`collapse-icon ${configIsOpen ? 'open' : ''}`}>
                                        {configIsOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                      </span>
                                    </div>

                                    {configIsOpen && (
                                      <div className="collapsible-content">
                                        <h5 className="section-title">בתי חולים</h5>
                                        <table className="hospitals-table">
                                          <thead>
                                            <tr>
                                              <th>בית חולים</th>
                                              <th>סטטוס</th>
                                              <th>פעולות</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {availableHospitals.map(hospital => {
                                              const hospitalExists = hospitals[hospital.name];

                                              return (
                                                <tr key={hospital.name}>
                                                  <td>{hospital.name}</td>
                                                  <td>
                                                    {hospitalExists ? (
                                                      <span className="status-exists">קיים</span>
                                                    ) : (
                                                      <span className="status-missing">לא קיים</span>
                                                    )}
                                                  </td>
                                                  <td className="actions-cell">
                                                    <div className="file-import-container">
                                                      <label
                                                        htmlFor={`import-${appName}-${configName}-${hospital.name}`}
                                                        className={`button-secondary${importing ? ' is-disabled' : ''}`}
                                                      >
                                                        {importing === `${appName}/${configName}/${hospital.name}` ? 'מייבא...' : 'ייבוא'}
                                                      </label>
                                                      <input
                                                        id={`import-${appName}-${configName}-${hospital.name}`}
                                                        type="file"
                                                        accept=".json,application/json"
                                                        disabled={!!importing}
                                                        onChange={(e) => handleImport(appName, configName, hospital.name, e)}
                                                        className="file-input"
                                                      />
                                                    </div>

                                                    {hospitalExists && (
                                                      <>
                                                        <button
                                                          className="button-secondary"
                                                          onClick={() => handleExport(appName, configName, hospital.name)}
                                                        >
                                                          ייצוא
                                                        </button>
                                                        <button
                                                          className="button-danger"
                                                          onClick={() => handleDelete(appName, configName, hospital.name)}
                                                        >
                                                          מחק
                                                        </button>
                                                      </>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </section>

          {/* Delete confirmation modal */}
          {deleteConfirmation && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3 className="modal-title">אישור מחיקה</h3>
                <p>האם אתה בטוח שברצונך למחוק את התצורה הבאה?</p>
                <p className="delete-details">
                  <strong>אפליקציה:</strong> {deleteConfirmation.appName}<br />
                  <strong>תצורה:</strong> {deleteConfirmation.configName}<br />
                  <strong>בית חולים:</strong> {availableHospitals.find(h => h.name === deleteConfirmation.hospital)?.nameHebrew ?? deleteConfirmation.hospital}
                </p>
                <p className="delete-warning">פעולה זו אינה ניתנת לביטול!</p>
                <div className="modal-actions">
                  <button className="button-danger" onClick={confirmDelete}>
                      כן, מחק
                    </button>
                  <button className="button-secondary" onClick={cancelDelete}>
                      ביטול
                    </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AppConfigSettings;